import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  alpha,
  Button,
  ClickAwayListener,
  Dialog,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import 'moment/locale/es';
import 'moment/locale/pt-br';
import 'moment/locale/fr';
import 'moment/locale/zh-cn';
import 'moment/locale/ru';
import 'moment/locale/hi';
import 'moment/locale/ar';
import { PhoneInput, defaultCountries, parseCountry, CountryData } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { PhoneNumberUtil } from 'google-libphonenumber';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import usFlag from 'assets/images/us-flag.svg';
import mxFlag from 'assets/images/mx-flag.svg';
import brFlag from 'assets/images/br-flag.svg';
import frFlag from 'assets/images/fr-flag.svg';
import zhFlag from 'assets/images/zh-flag.svg';
import ruFlag from 'assets/images/ru-flag.svg';
import hiFlag from 'assets/images/hi-flag.svg';
import arFlag from 'assets/images/ar-flag.svg';

import { ConfirmResultEnum } from 'context';
import { CalendarIcon, ChevronRightIcon, ClockIcon, CrossIcon } from 'assets';
import { FormInput, Spinner } from 'components';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { ApiUser } from 'types';
import { CreateCalendarInviteType, IApiRequests, useApiRequests, useCreateCalendarInvite } from 'hooks';
import { isHipaaLink } from '../../../../config/productProfile';
import { getMomentLocale } from 'utils';

import styles from './InviteClientPopup.module.scss';
import languageSelectorStyles from '../../../../components/LanguageSelector/LanguageSelector.module.scss';

type InviteType = 'sms' | 'email' | 'calendar';
type CalendarType = 'google' | 'outlook' | 'iCal';
type InviteLanguageCode = 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar';

type InviteClientFormType = {
  invitationMessage: string;
  email: string;
};

export type InviteClientPopupProps = {
  open: boolean;
  onClose(): void;
  onConfirm(confirmResult: ConfirmResultEnum): void;
};

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    return false;
  }
};

export function InviteClientPopup({ open, onClose, onConfirm }: InviteClientPopupProps) {
  const { t, i18n } = useTranslation('link');
  const { enqueueSnackbar } = useSnackbar();
  const { createGoogleEvent, createOutlookEvent, createICalEvent }: CreateCalendarInviteType =
    useCreateCalendarInvite();
  const { inviteToCallBySms, inviteToCallByEmail }: IApiRequests = useApiRequests();
  const user: ApiUser | null = useAppSelector(selectUser);
  const theme = useTheme();

  const calendarLocale = useMemo(() => {
    return getMomentLocale(i18n.resolvedLanguage || i18n.language || 'en');
  }, [i18n.language, i18n.resolvedLanguage]);

  const [loading, setLoading] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [languageTooltipOpen, setLanguageTooltipOpen] = useState<boolean>(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState<boolean>(false);
  const [selectedInviteType, setSelectedInviteType] = useState<InviteType>('email');
  const [selectedInviteLanguage, setSelectedInviteLanguage] = useState<InviteLanguageCode>('en');
  const [calendarType, setCalendarType] = useState<CalendarType>('google');
  const [dateSelectorIsOpen, setDateSelectorIsOpen] = useState<boolean>(false);
  const [dateSelected, setDateSelected] = useState<Moment>(moment());
  const [timeFromSelected, setTimeFromSelected] = useState<Moment>(
    moment([moment().year(), moment().month(), moment().date(), 10, 0]),
  );
  const [timeToSelected, setTimeToSelected] = useState<Moment>(
    moment([moment().year(), moment().month(), moment().date(), 11, 0]),
  );
  const [phone, setPhone] = useState<string>('');
  const [isPhoneInputTouched, setIsPhoneInputTouched] = useState<boolean>(false);
  const phoneIsValid: boolean = isPhoneValid(phone);
  const isAppRtl: boolean = i18n.dir() === 'rtl';
  const isInviteRtl: boolean = selectedInviteLanguage === 'ar';
  const inviteTypeMenuProps = useMemo(
    () => ({
      PaperProps: {
        sx: {
          '& .MuiMenuItem-root:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          '& .MuiMenuItem-root.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.14),
          },
          '& .MuiMenuItem-root.Mui-selected:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
          },
        },
      },
    }),
    [theme.palette.primary.main],
  );

  const getNormalizedInviteLanguage = useCallback(
    (rawLanguage?: string): InviteLanguageCode => {
      const locale = (rawLanguage || i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
      if (locale === 'es' || locale.startsWith('es-')) return 'es';
      if (locale === 'br' || locale === 'pt' || locale.startsWith('pt-')) return 'br';
      if (locale === 'fr' || locale.startsWith('fr-')) return 'fr';
      if (locale === 'zh' || locale.startsWith('zh-')) return 'zh';
      if (locale === 'ru' || locale.startsWith('ru-')) return 'ru';
      if (locale === 'hi' || locale.startsWith('hi-')) return 'hi';
      if (locale === 'ar' || locale.startsWith('ar-')) return 'ar';
      return 'en';
    },
    [i18n.language, i18n.resolvedLanguage],
  );

  const languageOptions: Array<{ code: InviteLanguageCode; label: string; flag: string }> = useMemo(
    () => [
      { code: 'en', label: 'English', flag: usFlag },
      { code: 'es', label: 'Español', flag: mxFlag },
      { code: 'br', label: 'Português', flag: brFlag },
      { code: 'fr', label: 'Français', flag: frFlag },
      { code: 'zh', label: '中文', flag: zhFlag },
      { code: 'ru', label: 'Русский', flag: ruFlag },
      { code: 'hi', label: 'हिन्दी', flag: hiFlag },
      { code: 'ar', label: 'العربية', flag: arFlag },
    ],
    [],
  );

  useEffect(() => {
    if (open) {
      setSelectedInviteLanguage(getNormalizedInviteLanguage());
    }
  }, [getNormalizedInviteLanguage, open]);

  const formatedSelectedDate: string = useMemo(() => {
    return dateSelected.clone().locale(calendarLocale).format('L');
  }, [calendarLocale, dateSelected]);

  const selectTimeOptions: { [key: string]: Moment } = useMemo(() => {
    const options: { [key: string]: Moment } = {};
    const tempTime: Moment = moment([dateSelected.year(), dateSelected.month(), dateSelected.date()]);
    const endOfDay = moment(dateSelected).endOf('day');

    while (tempTime.isSameOrBefore(endOfDay)) {
      options[tempTime.clone().locale('en').format('hh:mm A')] = tempTime.clone();
      tempTime.add(15, 'm');
    }

    return options;
  }, [calendarLocale, dateSelected]);

  const availableTimeFromSelectOptions: { [key: string]: string } = useMemo(() => {
    const options: { [key: string]: string } = {};
    const currentTime: Moment = moment();

    Object.keys(selectTimeOptions).forEach((key: string) => {
      const tempTime: Moment = selectTimeOptions[key];

      if (currentTime.isBefore(tempTime)) {
        options[key] = key;
      }
    });

    return options;
  }, [selectTimeOptions, dateSelected]);

  const fromDate: Moment = useMemo(() => {
    return moment([
      dateSelected.year(),
      dateSelected.month(),
      dateSelected.date(),
      timeFromSelected.hour(),
      timeFromSelected.minute(),
    ]);
  }, [dateSelected, timeFromSelected]);

  const availableTimeToSelectOptions: { [key: string]: string } = useMemo(() => {
    const options: { [key: string]: string } = {};

    let tempTime: Moment = moment();

    Object.keys(selectTimeOptions).forEach((key: string) => {
      tempTime = selectTimeOptions[key];

      if (fromDate.isBefore(tempTime)) {
        options[key] = key;
      }
    });

    return options;
  }, [selectTimeOptions, fromDate]);

  const toDate: Moment = useMemo(() => {
    return moment([
      dateSelected.year(),
      dateSelected.month(),
      dateSelected.date(),
      timeToSelected.hour(),
      timeToSelected.minute(),
    ]);
  }, [dateSelected, timeToSelected]);

  const userFullName: string = useMemo(() => {
    let fullName = user!.clinicUser.preferedDisplayName;
    if (!fullName) {
      fullName = [user!.title, user!.firstName, user!.lastName].filter((text: string | undefined) => !!text).join(' ');

      if (isHipaaLink && user?.clinicUser.suffix) {
        fullName += `, ${user?.clinicUser.suffix}`;
      }
    }

    return fullName;
  }, [user]);

  const inviteLinkWithLanguage = useMemo(() => {
    const roomName = (user?.clinicUser.roomName || '').replace(/^\/+/, '');
    const basePath = roomName ? `/${roomName}` : '';
    const languageSuffix = selectedInviteLanguage === 'en' ? '' : `?${selectedInviteLanguage}`;
    return `${window.location.origin}${basePath}${languageSuffix}`;
  }, [selectedInviteLanguage, user?.clinicUser.roomName]);

  const formatInvitationMessageForCalendar = useCallback(
    (message: string): string => {
      if (!isInviteRtl) {
        return message;
      }

      const [messageText, ...rest] = message.split('\n\n');
      const linkText = rest.join('\n\n');

      return `\u202B${messageText}\u202C\n\n\u200E${linkText}\u200E`;
    },
    [isInviteRtl],
  );

  const getInvitationMessage = useCallback(
    (languageCode: InviteLanguageCode) =>
      i18n.t('inviteClient.defaultInvitationMessage', {
        ns: 'link',
        lng: languageCode,
        userName: userFullName,
        joinLink: inviteLinkWithLanguage,
      }),
    [i18n, inviteLinkWithLanguage, userFullName],
  );

  const formDefaultValues = useMemo(
    () => ({
      invitationMessage: getInvitationMessage(selectedInviteLanguage),
    }),
    [getInvitationMessage, selectedInviteLanguage],
  );

  const schema = useMemo(
    () =>
      Yup.object().shape({
        invitationMessage: Yup.string()
          .max(500, t('inviteClient.validation.maxLength500'))
          .required(t('inviteClient.validation.invitationMessageRequired')),
        email: Yup.string()
          .email(t('inviteClient.validation.emailInvalid'))
          .required(t('inviteClient.validation.emailRequired')),
      }),
    [t],
  );

  const countriesForPhoneInput: CountryData[] = useMemo(() => {
    return defaultCountries.filter((country) => {
      const { iso2 } = parseCountry(country);
      return ['us'].includes(iso2);
    });
  }, []);

  const form = useForm<InviteClientFormType>({
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues,
    mode: 'all',
  });
  const emailValue: string = form.watch('email') || '';

  const buttonText: string = useMemo(() => {
    if (selectedInviteType === 'calendar') {
      return t('inviteClient.buttonLaunchCalendar');
    }

    return t('inviteClient.buttonSendInvitation');
  }, [selectedInviteType, t]);

  const lounchCalendar = useCallback((): void => {
    if (!isFormValid) {
      return;
    }

    void i18n.loadLanguages([selectedInviteLanguage]).finally(() => {
      const fixedT = i18n.getFixedT(selectedInviteLanguage, 'link');
      const invitationSubject = fixedT('inviteClient.onlineSessionWith', { userName: userFullName });
      const invitationMessage = fixedT('inviteClient.defaultInvitationMessage', {
        userName: userFullName,
        joinLink: inviteLinkWithLanguage,
      });
      const calendarInvitationMessage = formatInvitationMessageForCalendar(invitationMessage);

      switch (calendarType) {
        case 'google':
          createGoogleEvent(fromDate, toDate, invitationSubject, calendarInvitationMessage, inviteLinkWithLanguage);
          break;
        case 'outlook':
          createOutlookEvent(fromDate, toDate, invitationSubject, calendarInvitationMessage, inviteLinkWithLanguage);
          break;
        case 'iCal':
          createICalEvent(fromDate, toDate, invitationSubject, calendarInvitationMessage, inviteLinkWithLanguage);
          break;
      }
    });
  }, [
    calendarType,
    fromDate,
    toDate,
    userFullName,
    isFormValid,
    i18n,
    selectedInviteLanguage,
    createGoogleEvent,
    createOutlookEvent,
    createICalEvent,
    formatInvitationMessageForCalendar,
    inviteLinkWithLanguage,
  ]);

  const inviteBySms = useCallback((): void => {
    if (!isFormValid) {
      return;
    }

    setLoading(true);

    inviteToCallBySms({ phoneNumber: phone, languageCode: selectedInviteLanguage })
      .then(() => {
        onConfirm(ConfirmResultEnum.Confirm);
      })
      .catch((error: Error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isFormValid, phone, inviteToCallBySms, selectedInviteLanguage]);

  const inviteByEmail = useCallback((): void => {
    if (!isFormValid) {
      return;
    }

    setLoading(true);

    const formData: InviteClientFormType = form.getValues();

    inviteToCallByEmail({ email: formData.email, languageCode: selectedInviteLanguage })
      .then(() => {
        onConfirm(ConfirmResultEnum.Confirm);
      })
      .catch((error: Error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isFormValid, inviteToCallByEmail, selectedInviteLanguage]);

  useEffect(() => {
    const currentTime: Moment = moment();
    if (fromDate.isBefore(currentTime)) {
      const minutesToAdd: number = currentTime.minute() % 15;
      setTimeFromSelected(currentTime.add(15 - minutesToAdd, 'minute'));
    }
  }, [fromDate]);

  useEffect(() => {
    if (toDate.isSameOrBefore(fromDate)) {
      setTimeToSelected(fromDate.clone().add(15, 'minute'));
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (selectedInviteType === 'calendar') {
      setIsFormValid(true);
    } else if (selectedInviteType === 'sms') {
      setIsFormValid(phoneIsValid);
    } else {
      setIsFormValid(form.formState.isValid);
    }
  }, [selectedInviteType, phoneIsValid, form.formState.isValid]);

  useEffect(() => {
    let isMounted = true;
    i18n.loadLanguages([selectedInviteLanguage]).finally(() => {
      if (!isMounted) {
        return;
      }
      form.setValue('invitationMessage', getInvitationMessage(selectedInviteLanguage), { shouldValidate: true });
    });
    return () => {
      isMounted = false;
    };
  }, [form, getInvitationMessage, i18n, selectedInviteLanguage]);

  const handleSelectChange = (event: SelectChangeEvent<InviteType>): void => {
    setSelectedInviteType(event.target.value as InviteType);
  };

  const handleLanguageChange = (event: SelectChangeEvent<InviteLanguageCode>): void => {
    setSelectedInviteLanguage(event.target.value as InviteLanguageCode);
  };

  const handleOpenDateSelector = (): void => {
    setDateSelectorIsOpen(true);
  };

  const handleDateChange = (value: Moment | null): void => {
    setDateSelectorIsOpen(false);
    setDateSelected(value!);
  };

  const handleCloseDateSelector = (): void => {
    setDateSelectorIsOpen(false);
  };

  const handleTimeFromChange = (event: SelectChangeEvent<string>): void => {
    setTimeFromSelected(selectTimeOptions[event.target.value]);
  };

  const handleTimeToChange = (event: SelectChangeEvent<string>): void => {
    setTimeToSelected(selectTimeOptions[event.target.value]);
  };

  const handleChangeCalendarType = (event: ChangeEvent<HTMLInputElement>, value: string): void => {
    setCalendarType(value as CalendarType);
  };

  const handlePhoneCahnge = (phone: string): void => {
    setPhone(phone);
  };

  const handlePhoneInputBlur = (): void => {
    setIsPhoneInputTouched(true);
  };

  const handleSendInvite = useCallback((): void => {
    switch (selectedInviteType) {
      case 'calendar':
        lounchCalendar();
        break;
      case 'sms':
        inviteBySms();
        break;
      case 'email':
        inviteByEmail();
        break;
    }
  }, [selectedInviteType, lounchCalendar, inviteBySms, inviteByEmail]);

  return (
    <Dialog className={styles.dialog} classes={{ paper: styles.dialogPaper }} open={open} onClose={onClose}>
      {loading && <Spinner />}

      <div className={styles.header}>
        <div className={styles.leftHeaderSide}>
          <Typography variant='body4' className={styles.inviteViaLabel}>
            {t('inviteClient.inviteVia')}
          </Typography>
          <Select
            fullWidth
            variant='outlined'
            className={styles.select}
            classes={{ icon: styles.selectIcon }}
            IconComponent={ChevronRightIcon}
            value={selectedInviteType}
            onChange={handleSelectChange}
            MenuProps={inviteTypeMenuProps}
          >
            <MenuItem value={'email'}>{t('inviteClient.inviteTypeEmail')}</MenuItem>
            <MenuItem value={'sms'}>{t('inviteClient.inviteTypeSms')}</MenuItem>
            <MenuItem value={'calendar'}>{t('inviteClient.inviteTypeCalendar')}</MenuItem>
          </Select>
        </div>

        <CrossIcon className={styles.closeIcon} color='primary' onClick={onClose} />
      </div>

      {selectedInviteType === 'calendar' && (
        <>
          <div className={styles.dateSelectorRow}>
            <CalendarIcon fill='var(--app-surface)' stroke='var(--app-brand-primary)' />

            <div className={styles.openDateSelectorButton} onClick={handleOpenDateSelector}>
              {formatedSelectedDate}
            </div>

            {dateSelectorIsOpen && (
              <>
                <div className={styles.dateSelectorContainer}></div>

                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={calendarLocale}>
                  <ClickAwayListener onClickAway={handleCloseDateSelector}>
                    <DateCalendar
                      className={styles.dateSelector}
                      value={dateSelected}
                      onChange={handleDateChange}
                      minDate={moment()}
                      views={['day']}
                      showDaysOutsideCurrentMonth
                    />
                  </ClickAwayListener>
                </LocalizationProvider>
              </>
            )}
          </div>

          <div className={styles.timeSelectorRow}>
            <ClockIcon stroke='var(--app-brand-primary)' fill='var(--app-surface)' />

            <Select
              variant='outlined'
              className={styles.select}
              classes={{ icon: styles.selectIcon }}
              MenuProps={{ classes: { list: styles.timeSelectList } }}
              IconComponent={ChevronRightIcon}
              value={timeFromSelected.clone().locale('en').format('hh:mm A')}
              onChange={handleTimeFromChange}
              size='small'
            >
              {Object.keys(availableTimeFromSelectOptions).map((optionKey: string) => (
                <MenuItem value={optionKey} key={optionKey}>
                  {availableTimeFromSelectOptions[optionKey]}
                </MenuItem>
              ))}
            </Select>

            <Typography variant='body5'>{t('inviteClient.to')}</Typography>

            <Select
              variant='outlined'
              className={styles.select}
              classes={{ icon: styles.selectIcon }}
              MenuProps={{ classes: { list: styles.timeSelectList } }}
              IconComponent={ChevronRightIcon}
              value={timeToSelected.clone().locale('en').format('hh:mm A')}
              onChange={handleTimeToChange}
            >
              {Object.keys(availableTimeToSelectOptions).map((optionKey: string) => (
                <MenuItem value={optionKey} key={optionKey}>
                  {availableTimeToSelectOptions[optionKey]}
                </MenuItem>
              ))}
            </Select>
          </div>
        </>
      )}

      {selectedInviteType === 'sms' && (
        <div className={styles.row}>
          <Typography variant='body2'>
            {isHipaaLink ? t('inviteClient.clientPhoneNumber') : t('inviteClient.guestPhoneNumber')}
          </Typography>

          <div dir='ltr' className={styles.phoneInputLtrWrap}>
            <PhoneInput
              countries={countriesForPhoneInput}
              defaultCountry='us'
              disableDialCodePrefill
              disableDialCodeAndPrefix
              className={classnames({
                [styles.invalidPhone]: !phoneIsValid && isPhoneInputTouched,
                [styles.singleCountryPhone]: true,
              })}
              inputClassName={classnames(styles.phoneInput, {
                [styles.invalidPhoneInput]: !phoneIsValid && isPhoneInputTouched,
              })}
              placeholder='(xxx) xxx-xxxx'
              value={phone}
              onBlur={handlePhoneInputBlur}
              onChange={handlePhoneCahnge}
            />
          </div>

          {!phoneIsValid && isPhoneInputTouched && (
            <span className={styles.errorMessage}>{t('inviteClient.invalidPhoneNumber')}</span>
          )}
        </div>
      )}

      {selectedInviteType === 'email' && (
        <div className={styles.row}>
          <Typography variant='body2'>
            {isHipaaLink ? t('inviteClient.clientEmail') : t('inviteClient.guestEmail')}
          </Typography>

          <FormInput
            name='email'
            control={form.control}
            className={styles.emailInput}
            errorHelperClassName={styles.errorHelper}
            placeholder={t('inviteClient.enterEmailAddress')}
            inputProps={{ dir: 'ltr', style: { textAlign: isAppRtl && !emailValue ? 'right' : 'left' } }}
          />
        </div>
      )}

      <div className={styles.row}>
        <div className={styles.invitationHeader}>
          <Typography variant='body2'>{t('inviteClient.invitationMessage')}</Typography>

          <Tooltip
            title={t('inviteClient.inviteLanguage')}
            placement='top'
            open={languageTooltipOpen && !languageMenuOpen}
            onOpen={() => setLanguageTooltipOpen(true)}
            onClose={() => setLanguageTooltipOpen(false)}
          >
            <div
              className={styles.invitationLanguageButtonWrap}
              onMouseDown={() => setLanguageTooltipOpen(false)}
              onClick={() => setLanguageTooltipOpen(false)}
            >
              <Select
                variant='outlined'
                className={styles.languageSelect}
                classes={{ icon: styles.selectIcon }}
                IconComponent={KeyboardArrowDown}
                value={selectedInviteLanguage}
                onChange={handleLanguageChange}
                onOpen={() => {
                  setLanguageTooltipOpen(false);
                  setLanguageMenuOpen(true);
                }}
                onClose={() => {
                  setLanguageMenuOpen(false);
                  setLanguageTooltipOpen(false);
                }}
                MenuProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: isInviteRtl ? 'left' : 'right' },
                  transformOrigin: { vertical: 'top', horizontal: isInviteRtl ? 'left' : 'right' },
                  MenuListProps: { disablePadding: true },
                  PaperProps: {
                    sx: {
                      width: 'max-content',
                      minWidth: '128px',
                      maxWidth: '185px',
                      boxSizing: 'border-box',
                      marginTop: '-1px',
                      borderTop: 'none',
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      '& .MuiMenuItem-root:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                      '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.14),
                      },
                      '& .MuiMenuItem-root.Mui-selected:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      },
                    },
                  },
                }}
                renderValue={(value) => {
                  const selected = languageOptions.find((option) => option.code === value);
                  return (
                    <div className={styles.languageIconOnly}>
                      <img
                        src={selected?.flag}
                        alt=''
                        className={classnames(styles.languageFlag, languageSelectorStyles.flag)}
                      />
                    </div>
                  );
                }}
              >
                {languageOptions.map((option) => (
                  <MenuItem
                    key={option.code}
                    value={option.code}
                    className={languageSelectorStyles.menuItem}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '0.6rem 1rem',
                      whiteSpace: 'nowrap',
                      direction: 'ltr',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      gap: '8px',
                      alignItems: 'center',
                      [`& .${styles.languageOption}`]: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        whiteSpace: 'nowrap',
                      },
                      [`& .${languageSelectorStyles.menuFlag}`]: {
                        margin: 0,
                        marginInlineEnd: '0.8rem',
                      },
                      [`& .${languageSelectorStyles.menuLabel}`]: {
                        textAlign: 'left',
                      },
                    }}
                  >
                    <div className={styles.languageOption}>
                      <img
                        src={option.flag}
                        alt=''
                        className={classnames(styles.languageFlag, languageSelectorStyles.menuFlag)}
                      />
                      <span className={languageSelectorStyles.menuLabel}>{option.label}</span>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </div>
          </Tooltip>
        </div>

        <FormInput
          name='invitationMessage'
          disabled={true}
          dir={isInviteRtl ? 'rtl' : 'ltr'}
          control={form.control}
          multiline
          maxRows={4}
          className={styles.textareas}
          errorHelperClassName={styles.errorHelper}
          inputProps={{ style: { textAlign: isInviteRtl ? 'right' : 'left' } }}
        />
      </div>

      {selectedInviteType === 'calendar' && (
        <RadioGroup
          name='selectCalendarType'
          value={calendarType}
          className={styles.radioGroup}
          onChange={handleChangeCalendarType}
        >
          <FormControlLabel
            value={'google'}
            control={<Radio className={styles.check} />}
            label={t('inviteClient.googleCalendar')}
          />
          <FormControlLabel
            value={'outlook'}
            control={<Radio className={styles.check} />}
            label={t('inviteClient.outlook365')}
          />
          <FormControlLabel
            value={'iCal'}
            control={<Radio className={styles.check} />}
            label={t('inviteClient.defaultCalendar')}
          />
        </RadioGroup>
      )}

      <Button
        variant='contained'
        fullWidth
        onClick={handleSendInvite}
        disabled={!isFormValid}
        sx={{ color: '#FFFFFF' }}
      >
        {buttonText}
      </Button>
    </Dialog>
  );
}
